import React, { useState, useEffect, useCallback } from 'react';
import { useModalDismiss } from '../../hooks/useModalDismiss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileExcel,
  faMoneyBillWave,
  faClipboardCheck,
  faPauseCircle,
  faGift,
  faRotate,
  faChevronDown,
  faChevronUp,
  faXmark,
  faArrowUpRightFromSquare,
} from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';
import SearchableSelect from '../../components/SearchableSelect';

const TABS = [
  { key: 'survey', label: 'Par enquête' },
  { key: 'controller', label: 'Par contrôleur' },
  { key: 'overview', label: "Vue d'ensemble" },
];

const firstOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};
const today = () => new Date().toISOString().slice(0, 10);

const fmtMad = (n) =>
  (Number(n) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' MAD';
const fmtNum = (n) => (Number(n) || 0).toLocaleString('fr-FR');

const ControllerStats = () => {
  const [fromDate, setFromDate] = useState(firstOfMonth());
  const [toDate, setToDate] = useState(today());
  const [surveyId, setSurveyId] = useState(null);
  const [source, setSource] = useState('all'); // all | cati | f2f
  const [surveys, setSurveys] = useState([]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  const [activeTab, setActiveTab] = useState('survey');
  const [expanded, setExpanded] = useState({}); // row expand state
  const [showControlCost, setShowControlCost] = useState(false); // Coût total card flip
  const [bonusModal, setBonusModal] = useState(null); // { name, total, details } | null

  useEffect(() => {
    api
      .get('/control/surveys', { params: { per_page: 1000 } })
      .then((res) => setSurveys(res.data?.data || res.data || []))
      .catch(() => setSurveys([]));
  }, []);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        from_date: fromDate,
        to_date: toDate,
        source,
        include: 'per_survey,per_controller,other',
      };
      if (surveyId) params.survey_id = surveyId;
      const res = await api.get('/control/controller-stats', { params });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.message || 'Erreur lors du chargement des statistiques');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, source, surveyId]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ from_date: fromDate, to_date: toDate, source });
      if (surveyId) params.append('survey_id', surveyId);
      const res = await api.get(`/control/controller-stats/export?${params.toString()}`, {
        responseType: 'blob',
        timeout: 300000,
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const cd = res.headers['content-disposition'];
      let filename = 'activite_controleurs.xlsx';
      if (cd) {
        const m = cd.match(/filename="?([^"]+)"?/i);
        if (m) filename = m[1];
      }
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError('Erreur lors de l\'export');
    } finally {
      setExporting(false);
    }
  };

  const g = data?.global;
  const perSurvey = data?.per_survey || [];
  const perController = data?.per_controller || [];
  const other = data?.other_bucket;

  const toggle = (key) => setExpanded((e) => ({ ...e, [key]: !e[key] }));

  return (
    <div className="p-6 max-w-[1500px] mx-auto">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Activité &amp; coût des contrôleurs</h1>
          <p className="text-sm text-gray-500">
            Suivi de l'activité des contrôleurs, du coût et du temps de contrôle par enquête.
          </p>
        </div>
        <button
          onClick={handleExport}
          disabled={exporting || loading}
          className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium"
        >
          <FontAwesomeIcon icon={faFileExcel} />
          {exporting ? 'Export…' : 'Exporter'}
        </button>
      </div>

      {/* Filter bar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-5">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Du</label>
            <input
              type="date"
              value={fromDate}
              max={toDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Au</label>
            <input
              type="date"
              value={toDate}
              min={fromDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Enquête</label>
            <SearchableSelect
              options={surveys}
              selectedValue={surveyId}
              onChange={(val) => setSurveyId(val)}
              placeholder="Toutes les enquêtes"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Source</label>
            <div className="flex w-full rounded-lg border border-gray-300 overflow-hidden divide-x divide-gray-300">
              {['all', 'cati', 'f2f'].map((s) => (
                <button
                  key={s}
                  onClick={() => setSource(s)}
                  className={`flex-1 px-4 py-3 text-sm transition-colors ${
                    source === s ? 'bg-red-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {s === 'all' ? 'Toutes' : s.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg mb-4 text-sm">
          {error}
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
        <Kpi icon={faClipboardCheck} label="Contrôles" value={fmtNum(g?.unique_submissions)} loading={loading} />
        <FlipKpi
          flipped={showControlCost}
          onClick={() => setShowControlCost((v) => !v)}
          loading={loading}
          front={{ icon: faMoneyBillWave, label: 'Coût total', value: fmtMad(g?.total_cost_mad) }}
          back={{ icon: faMoneyBillWave, label: 'Coût contrôle', value: fmtMad(g?.control_cost_mad) }}
        />
        <Kpi icon={faMoneyBillWave} label="Coût / contrôle" value={fmtMad(g?.cost_per_control_mad)} loading={loading} />
        <Kpi icon={faGift} label="Bonus contrôleurs" value={fmtMad(g?.controller_bonuses_mad)} loading={loading} />
        <Kpi icon={faPauseCircle} label="Hors contrôle" value={fmtMad(other?.cost_mad)} loading={loading} />
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-4 flex gap-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeTab === t.key
                ? 'border-red-600 text-red-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading && <div className="text-center text-gray-400 py-10">Chargement…</div>}

      {!loading && activeTab === 'survey' && (
        <SurveyTable rows={perSurvey} expanded={expanded} toggle={toggle} />
      )}
      {!loading && activeTab === 'controller' && (
        <ControllerTable rows={perController} expanded={expanded} toggle={toggle} onBonusClick={setBonusModal} />
      )}
      {!loading && activeTab === 'overview' && (
        <Overview perSurvey={perSurvey} perController={perController} />
      )}

      <BonusModal data={bonusModal} onClose={() => setBonusModal(null)} />
    </div>
  );
};

const KpiFace = ({ icon, label, value, loading, hint }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 flex items-center gap-3 w-full h-full">
    <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-red-50 text-red-600 shrink-0">
      <FontAwesomeIcon icon={icon} />
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-xs text-gray-500 truncate flex items-center gap-1">
        {label}
        {hint && <FontAwesomeIcon icon={faRotate} className="text-gray-300 text-[10px]" />}
      </div>
      <div className="text-base font-semibold text-gray-800 truncate">{loading ? '…' : value}</div>
    </div>
  </div>
);

const Kpi = (props) => <KpiFace {...props} />;

// Clickable 3D flip card: front/back are KpiFace prop objects.
const FlipKpi = ({ flipped, onClick, front, back, loading }) => (
  <div
    onClick={onClick}
    title="Cliquer pour basculer"
    className="[perspective:1000px] cursor-pointer select-none"
  >
    <div
      className={`relative w-full h-full transition-transform duration-500 [transform-style:preserve-3d] ${
        flipped ? '[transform:rotateY(180deg)]' : ''
      }`}
    >
      <div className="[backface-visibility:hidden]">
        <KpiFace {...front} loading={loading} hint />
      </div>
      <div className="absolute inset-0 [transform:rotateY(180deg)] [backface-visibility:hidden]">
        <KpiFace {...back} loading={loading} hint />
      </div>
    </div>
  </div>
);

const Bar = ({ pct, color = 'bg-red-500' }) => (
  <div className="w-full bg-gray-100 rounded h-2 overflow-hidden">
    <div className={`h-2 ${color}`} style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
  </div>
);

const ActionMix = ({ mix }) => {
  const v = Math.round((mix?.validate || 0) * 100);
  const r = Math.round((mix?.reject || 0) * 100);
  const h = Math.round((mix?.hold || 0) * 100);
  return (
    <div className="flex w-28 h-2 rounded overflow-hidden bg-gray-100" title={`Validé ${v}% · Rejeté ${r}% · Attente ${h}%`}>
      <div className="bg-green-500 h-2" style={{ width: `${v}%` }} />
      <div className="bg-red-500 h-2" style={{ width: `${r}%` }} />
      <div className="bg-amber-400 h-2" style={{ width: `${h}%` }} />
    </div>
  );
};

const EmptyRow = ({ cols }) => (
  <tr>
    <td colSpan={cols} className="text-center text-gray-400 py-8">
      Aucune donnée pour cette période.
    </td>
  </tr>
);

const SurveyTable = ({ rows, expanded, toggle }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
    <table className="min-w-full text-sm">
      <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
        <tr>
          <th className="px-3 py-3 text-left">Enquête</th>
          <th className="px-3 py-3 text-center">Type</th>
          <th className="px-3 py-3 text-right">Soumissions</th>
          <th className="px-3 py-3 text-right">Coût</th>
          <th className="px-3 py-3 text-right">Coût/contrôle</th>
          <th className="px-3 py-3 text-right">Jours-ctrl</th>
          <th className="px-3 py-3 text-left w-40">Couverture</th>
          <th className="px-3 py-3 text-center">Période</th>
          <th className="px-3 py-3" />
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {rows.length === 0 && <EmptyRow cols={9} />}
        {rows.map((s) => {
          const key = `s${s.survey_id}`;
          return (
            <React.Fragment key={key}>
              <tr className="hover:bg-gray-50">
                <td className="px-3 py-2 font-medium text-gray-800">
                  {s.title}
                  <span className="text-gray-400 text-xs ml-1">({s.limesurvey_id})</span>
                </td>
                <td className="px-3 py-2 text-center">
                  <span className={`text-xs px-2 py-0.5 rounded ${s.is_f2f ? 'bg-gray-100 text-gray-600' : 'bg-red-50 text-red-600'}`}>
                    {s.is_f2f ? 'F2F' : 'CATI'}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">{fmtNum(s.unique_submissions)}</td>
                <td className="px-3 py-2 text-right font-medium">
                  {fmtMad(s.total_cost_mad)}
                  {s.bonus_mad > 0 && (
                    <div className="text-[10px] font-normal text-gray-400">dont {fmtMad(s.bonus_mad)} bonus</div>
                  )}
                </td>
                <td className="px-3 py-2 text-right">{fmtMad(s.cost_per_control_mad)}</td>
                <td className="px-3 py-2 text-right">{fmtNum(s.controller_days)}</td>
                <td className="px-3 py-2">
                  {s.coverage_pct == null ? (
                    <span className="text-gray-400 text-xs">objectif non défini</span>
                  ) : (
                    <div>
                      <Bar pct={s.coverage_pct} color="bg-emerald-500" />
                      <span className="text-xs text-gray-500">
                        {s.controlled}/{s.control_target} · {s.coverage_pct}%
                      </span>
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-center text-xs text-gray-500">
                  {s.first_control_at} → {s.last_control_at}
                </td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => toggle(key)} className="text-gray-400 hover:text-gray-600">
                    <FontAwesomeIcon icon={expanded[key] ? faChevronUp : faChevronDown} />
                  </button>
                </td>
              </tr>
              {expanded[key] && (
                <tr className="bg-gray-50">
                  <td colSpan={9} className="px-6 py-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-gray-500">Contributeurs</span>
                      <span className="text-xs text-gray-400">
                        Coût contrôle : {fmtMad(s.cost_mad)} · Bonus : {fmtMad(s.bonus_mad)} · Total : {fmtMad(s.total_cost_mad)}
                      </span>
                    </div>
                    <table className="w-full text-xs">
                      <thead className="text-gray-400">
                        <tr>
                          <th className="text-left py-1">Contrôleur</th>
                          <th className="text-right py-1">Soumissions</th>
                          <th className="text-right py-1 text-green-600">Validés</th>
                          <th className="text-right py-1 text-red-600">Rejetés</th>
                          <th className="text-right py-1 text-amber-600">En attente</th>
                          <th className="text-right py-1">Coût</th>
                          <th className="text-right py-1">Bonus</th>
                          <th className="text-right py-1">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {s.contributors.map((c) => (
                          <tr key={c.controller_id}>
                            <td className="py-1">{c.name}</td>
                            <td className="py-1 text-right">{fmtNum(c.subs)}</td>
                            <td className="py-1 text-right text-green-600">{fmtNum(c.validate)}</td>
                            <td className="py-1 text-right text-red-600">{fmtNum(c.reject)}</td>
                            <td className="py-1 text-right text-amber-600">{fmtNum(c.hold)}</td>
                            <td className="py-1 text-right">{fmtMad(c.cost_mad)}</td>
                            <td className="py-1 text-right">{c.bonus_mad > 0 ? fmtMad(c.bonus_mad) : '—'}</td>
                            <td className="py-1 text-right font-medium">{fmtMad(c.total_cost_mad)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  </div>
);

const ControllerTable = ({ rows, expanded, toggle, onBonusClick }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
    <table className="min-w-full text-sm">
      <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
        <tr>
          <th className="px-3 py-3 text-left">Contrôleur</th>
          <th className="px-3 py-3 text-right">Soumissions</th>
          <th className="px-3 py-3 text-right">Jours</th>
          <th className="px-3 py-3 text-right">Paie</th>
          <th className="px-3 py-3 text-right">Bonus</th>
          <th className="px-3 py-3 text-right">Total</th>
          <th className="px-3 py-3 text-right">Moy./jour</th>
          <th className="px-3 py-3 text-center">Répartition</th>
          <th className="px-3 py-3 text-center">Note moy.</th>
          <th className="px-3 py-3 text-right">Attente</th>
          <th className="px-3 py-3" />
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {rows.length === 0 && <EmptyRow cols={11} />}
        {rows.map((c) => {
          const key = `c${c.controller_id}`;
          return (
            <React.Fragment key={key}>
              <tr className="hover:bg-gray-50">
                <td className="px-3 py-2 font-medium text-gray-800">
                  {c.name}
                  <span className="text-gray-400 text-xs ml-1">{c.ref}</span>
                  {c.status && c.status !== 'active' && (
                    <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{c.status}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">{fmtNum(c.unique_submissions)}</td>
                <td className="px-3 py-2 text-right">{fmtNum(c.days_worked)}</td>
                <td className="px-3 py-2 text-right font-medium">
                  {fmtMad(c.pay_mad)}
                  {c.tasks_estimated_cost_mad > 0 && (
                    <div className="text-[10px] font-normal text-gray-400">dont {fmtMad(c.tasks_estimated_cost_mad)} tâches</div>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  {c.bonus_mad > 0 ? (
                    <button
                      onClick={() => onBonusClick({ name: c.name, total: c.bonus_mad, details: c.bonus_details || [] })}
                      className="text-green-600 hover:underline font-medium"
                      title="Voir le détail des bonus"
                    >
                      +{fmtMad(c.bonus_mad)}
                    </button>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right font-medium">{fmtMad((c.pay_mad || 0) + (c.bonus_mad || 0))}</td>
                <td className="px-3 py-2 text-right">{c.avg_per_working_day}</td>
                <td className="px-3 py-2"><div className="flex justify-center"><ActionMix mix={c.action_mix} /></div></td>
                <td className="px-3 py-2 text-center">
                  {c.avg_rating_given == null ? (
                    <span className="text-gray-400">—</span>
                  ) : (
                    c.avg_rating_given
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  {c.open_holds > 0 ? (
                    <span className="text-amber-600">{c.open_holds}</span>
                  ) : (
                    <span className="text-gray-300">0</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => toggle(key)} className="text-gray-400 hover:text-gray-600">
                    <FontAwesomeIcon icon={expanded[key] ? faChevronUp : faChevronDown} />
                  </button>
                </td>
              </tr>
              {expanded[key] && (
                <tr className="bg-gray-50">
                  <td colSpan={11} className="px-6 py-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-gray-500">Coût par enquête</span>
                      {c.bonus_mad > 0 && (
                        <span className="text-xs text-gray-400">Bonus total : {fmtMad(c.bonus_mad)}</span>
                      )}
                    </div>
                    <table className="w-full text-xs mb-4">
                      <thead className="text-gray-400">
                        <tr>
                          <th className="text-left py-1">Enquête</th>
                          <th className="text-right py-1">Soumissions</th>
                          <th className="text-right py-1 text-green-600">Validés</th>
                          <th className="text-right py-1 text-red-600">Rejetés</th>
                          <th className="text-right py-1 text-amber-600">En attente</th>
                          <th className="text-right py-1">Coût</th>
                          <th className="text-right py-1">Bonus</th>
                          <th className="text-right py-1">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {c.surveys.map((s) => (
                          <tr key={s.survey_id}>
                            <td className="py-1">{s.title}</td>
                            <td className="py-1 text-right">{fmtNum(s.subs)}</td>
                            <td className="py-1 text-right text-green-600">{fmtNum(s.validate)}</td>
                            <td className="py-1 text-right text-red-600">{fmtNum(s.reject)}</td>
                            <td className="py-1 text-right text-amber-600">{fmtNum(s.hold)}</td>
                            <td className="py-1 text-right">{fmtMad(s.cost_mad)}</td>
                            <td className="py-1 text-right">{s.bonus_mad > 0 ? fmtMad(s.bonus_mad) : '—'}</td>
                            <td className="py-1 text-right font-medium">{fmtMad(s.total_cost_mad)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {c.tasks && c.tasks.length > 0 && (
                      <>
                        <div className="text-xs font-semibold text-gray-500 mb-2 flex items-center justify-between">
                          <span>Autres tâches</span>
                          <span className="text-gray-400 font-normal">
                            Coût estimé : {fmtMad(c.tasks_estimated_cost_mad)}
                          </span>
                        </div>
                        <table className="w-full text-xs">
                          <thead className="text-gray-400">
                            <tr>
                              <th className="text-left py-1">Date</th>
                              <th className="text-left py-1">Journée</th>
                              <th className="text-left py-1">Tâches</th>
                              <th className="text-left py-1">Commentaire</th>
                              <th className="text-right py-1">Coût estimé</th>
                            </tr>
                          </thead>
                          <tbody>
                            {c.tasks.map((t, i) => (
                              <tr key={i}>
                                <td className="py-1">{t.date}</td>
                                <td className="py-1">{t.day_type === 'full_day' ? 'Complète' : 'Demi'}</td>
                                <td className="py-1">{(t.raisons || []).join(', ')}</td>
                                <td className="py-1 text-gray-500">{t.comment || '—'}</td>
                                <td className="py-1 text-right">
                                  {t.had_controls ? (
                                    <span className="text-gray-400" title="Journée mixte : déjà payée via les contrôles">
                                      incluse
                                    </span>
                                  ) : (
                                    fmtMad(t.estimated_cost_mad)
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <p className="text-[11px] text-gray-400 mt-1">
                          Estimation : une journée de tâches sans contrôle = sa paie complète ; une journée mixte est
                          déjà comptabilisée dans le coût des contrôles.
                        </p>
                      </>
                    )}
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  </div>
);

const Overview = ({ perSurvey, perController }) => {
  const maxCost = Math.max(1, ...perSurvey.map((s) => s.total_cost_mad));
  const leaderboard = [...perController].sort((a, b) => b.unique_submissions - a.unique_submissions).slice(0, 10);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <Panel title="Coût par enquête">
        {perSurvey.length === 0 && <div className="text-gray-400 text-sm py-4">Aucune donnée.</div>}
        {perSurvey.slice(0, 12).map((s) => (
          <div key={s.survey_id} className="mb-2">
            <div className="flex justify-between text-xs mb-0.5">
              <span className="truncate pr-2 text-gray-700">{s.title}</span>
              <span className="text-gray-500 whitespace-nowrap">{fmtMad(s.total_cost_mad)}</span>
            </div>
            <Bar pct={(s.total_cost_mad / maxCost) * 100} color="bg-red-500" />
          </div>
        ))}
      </Panel>

      <Panel title="Classement contrôleurs (contrôles)">
        {leaderboard.length === 0 && <div className="text-gray-400 text-sm py-4">Aucune donnée.</div>}
        <table className="w-full text-sm">
          <tbody className="divide-y divide-gray-100">
            {leaderboard.map((c, i) => (
              <tr key={c.controller_id}>
                <td className="py-1.5 text-gray-400 w-6">{i + 1}</td>
                <td className="py-1.5 text-gray-700">{c.name}</td>
                <td className="py-1.5 text-right font-medium">{fmtNum(c.unique_submissions)}</td>
                <td className="py-1.5 text-right text-gray-500">{fmtMad(c.pay_mad)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

    </div>
  );
};

const Panel = ({ title, children }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
    <h3 className="text-sm font-semibold text-gray-700 mb-3">{title}</h3>
    {children}
  </div>
);

const BonusModal = ({ data, onClose }) => {
  const dismiss = useModalDismiss(onClose);
  if (!data) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      {...dismiss}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div>
            <h3 className="font-semibold text-gray-800">Bonus — {data.name}</h3>
            <p className="text-xs text-gray-500">Total : {fmtMad(data.total)}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
        <div className="p-4 space-y-2 overflow-y-auto">
          {(!data.details || data.details.length === 0) && (
            <div className="text-sm text-gray-400 text-center py-4">Aucun détail disponible.</div>
          )}
          {(data.details || []).map((b, i) => (
            <div key={i} className="border border-gray-100 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <span className="text-green-600 font-medium">+{fmtMad(b.amount)}</span>
                <span className="text-xs text-gray-400">
                  {b.period_start} → {b.period_end}
                </span>
              </div>
              <div className="text-sm text-gray-700 mt-1">
                {b.reason || <span className="text-gray-400">Sans raison</span>}
              </div>
              {b.survey_title && (
                <div className="text-xs text-gray-400 mt-0.5">Enquête : {b.survey_title}</div>
              )}
              <a
                href={`/payments/${b.batch_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline mt-2"
              >
                Voir le lot de paiement
                <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="text-[10px]" />
              </a>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ControllerStats;
